import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { wallsApi } from '@walls/services/wallsApi';
import type { WallBoard, WallCard, WallConnection, WallShape } from '@walls/store';
import { CardOwnerLabel } from '../cards/CardOwnerLabel';
import {
  getCardFrameDimensions,
  defaultRectangleCardWidth,
  defaultShapeCardSize,
} from '../cards/utils/cardGeometry';
import {
  getCardHtml,
  getCardShape,
  getCardText,
  getCardTitleHtml,
  getCardTitleText,
  isDarkCard,
  isImageCard,
} from '../cards/utils/cardDisplay';
import { loadCachedImage } from '../cards/utils/imageCache';
import { MultilineText } from '../cards/utils/MultilineText';
import { getConnectorPoints, getRenderedConnections } from '../connections/connectionGeometry';
import { hexToRgba } from '../drawing/utils/color';
import {
  getLineAttributes,
  getRectangleAttributes,
  getShapeType,
  getTextAttributes,
} from '../drawing/utils/shapeAttributes';
import { getClusterChildLayout, getPositionedCardDimensions } from '../positioning/cardRects';
import { sortByZPosition, getBoardCardTree, isClusterCard } from '../selectors/boardCardSelectors';
import { measureTextShape } from '../shapes/geometry/textGeometry';
import type { ExportBounds } from './exportGeometry';

interface ExportWallSnapshotProps {
  board: WallBoard;
  bounds: ExportBounds;
  cards: WallCard[];
  connections: WallConnection[];
  shapes: WallShape[];
}

export function ExportWallSnapshot({
  board,
  bounds,
  cards,
  connections,
  shapes,
}: ExportWallSnapshotProps) {
  const transform = `translate(${-bounds.x}px, ${-bounds.y}px)`;
  const orderedRootCards = useMemo(() => {
    const { rootCards, rootClusters } = getBoardCardTree(cards);

    return sortByZPosition([...rootClusters, ...rootCards]);
  }, [cards]);

  return (
    <Box
      data-wall-export-snapshot="true"
      sx={{
        bgcolor: '#ffffff',
        height: bounds.height,
        overflow: 'hidden',
        position: 'relative',
        width: bounds.width,
      }}
    >
      <Box sx={{ height: bounds.height, position: 'absolute', width: bounds.width, transform }}>
        <ExportShapesLayer bounds={bounds} shapes={shapes} />
        <ExportConnectionsLayer cards={cards} connections={connections} />
        {orderedRootCards.map((card) =>
          isClusterCard(card) ? (
            <ExportClusterCard key={card.id} board={board} card={card} />
          ) : (
            <ExportCard key={card.id} board={board} card={card} />
          )
        )}
      </Box>
    </Box>
  );
}

function ExportConnectionsLayer({
  cards,
  connections,
}: {
  cards: WallCard[];
  connections: WallConnection[];
}) {
  const renderedConnections = useMemo(
    () => getRenderedConnections(cards, connections),
    [cards, connections]
  );

  if (renderedConnections.length === 0) {
    return null;
  }

  return (
    <svg
      aria-hidden="true"
      style={{
        height: '100%',
        left: 0,
        overflow: 'visible',
        pointerEvents: 'none',
        position: 'absolute',
        top: 0,
        width: '100%',
        zIndex: 2,
      }}
    >
      {renderedConnections.map(({ connection, linePoints }) => (
        <polyline
          key={connection.id}
          fill="none"
          points={getConnectorPoints(linePoints).join(' ')}
          stroke={connection.color || '#000000'}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      ))}
    </svg>
  );
}

function ExportShapesLayer({ bounds, shapes }: { bounds: ExportBounds; shapes: WallShape[] }) {
  if (shapes.length === 0) {
    return null;
  }

  return (
    <svg
      aria-hidden="true"
      style={{
        height: bounds.height,
        left: bounds.x,
        overflow: 'visible',
        pointerEvents: 'none',
        position: 'absolute',
        top: bounds.y,
        width: bounds.width,
        zIndex: 1,
      }}
      viewBox={`${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`}
    >
      {[...shapes]
        .sort((left, right) => (left.zPos ?? 0) - (right.zPos ?? 0))
        .map((shape) => (
          <ExportShape key={shape.id} shape={shape} />
        ))}
    </svg>
  );
}

function ExportShape({ shape }: { shape: WallShape }) {
  const shapeType = getShapeType(shape);

  if (shapeType === 'line') {
    const attributes = getLineAttributes(shape);

    return (
      <line
        x1={attributes.startX}
        y1={attributes.startY}
        x2={attributes.endX}
        y2={attributes.endY}
        stroke={hexToRgba(attributes.lineColor, attributes.lineOpacity)}
        strokeLinecap="round"
        strokeWidth={attributes.lineThickness}
      />
    );
  }

  if (shapeType === 'text') {
    const attributes = getTextAttributes(shape);
    const measured = measureTextShape(shape);

    return (
      <text
        x={attributes.x}
        y={attributes.y}
        fill={attributes.color}
        fontFamily={attributes.fontFamily}
        fontSize={attributes.fontSize}
        dominantBaseline="text-before-edge"
      >
        {measured.lines.map((line, index) => (
          <tspan key={index} x={attributes.x} dy={index === 0 ? 0 : measured.lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    );
  }

  const attributes = getRectangleAttributes(shape);
  const commonProps = {
    fill: attributes.fillColor
      ? hexToRgba(attributes.fillColor, attributes.fillOpacity)
      : 'transparent',
    stroke: hexToRgba(attributes.lineColor, attributes.lineOpacity),
    strokeWidth: attributes.lineThickness,
  };

  if (shapeType === 'circle') {
    return (
      <ellipse
        cx={attributes.x + attributes.width / 2}
        cy={attributes.y + attributes.height / 2}
        rx={attributes.width / 2}
        ry={attributes.height / 2}
        {...commonProps}
      />
    );
  }

  return (
    <rect
      x={attributes.x}
      y={attributes.y}
      width={attributes.width}
      height={attributes.height}
      {...commonProps}
    />
  );
}

function ExportClusterCard({ board, card }: { board: WallBoard; card: WallCard }) {
  const dimensions = getPositionedCardDimensions(card);
  const children = getClusterChildLayout(card);
  const ownerName = getOwnerName(board, card);

  return (
    <Box
      data-element-id={card.id}
      sx={{
        bgcolor: '#f8fafc',
        border: `2px solid ${card.color || '#d4d9de'}`,
        borderRadius: '8px',
        boxShadow: card.isLocked ? 'none' : '0 1px 1px rgba(0, 0, 0, 0.15)',
        color: '#222',
        height: dimensions.height,
        left: card.xPos ?? 0,
        overflow: 'hidden',
        position: 'absolute',
        top: card.yPos ?? 0,
        width: dimensions.width,
        zIndex: card.isLocked ? Math.max(card.zPos ?? 0, 3) : card.zPos ?? 3,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: card.color || '#dbeafe',
          display: 'flex',
          height: 58,
          px: 1.5,
        }}
      >
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 600, wordBreak: 'break-word' }}
        >
          <MultilineText text={getCardText(card)} />
        </Typography>
      </Box>
      {!card.collapsed && (
        <Box sx={{ inset: 0, position: 'absolute' }}>
          {children.map(({ card: childCard, rect }) => (
            <Box
              key={childCard.id}
              sx={{
                height: rect.height,
                left: rect.x,
                position: 'absolute',
                top: rect.y,
                width: rect.width,
              }}
            >
              {isClusterCard(childCard) ? (
                <ExportNestedClusterCard board={board} card={childCard} />
              ) : (
                <ExportCard board={board} card={childCard} isClusterChild />
              )}
            </Box>
          ))}
        </Box>
      )}
      <CardOwnerLabel ownerName={ownerName} />
    </Box>
  );
}

function ExportNestedClusterCard({ board, card }: { board: WallBoard; card: WallCard }) {
  const dimensions = getPositionedCardDimensions(card);

  return (
    <Box
      sx={{
        bgcolor: '#f8fafc',
        border: `2px solid ${card.color || '#d4d9de'}`,
        borderRadius: '8px',
        height: dimensions.height,
        overflow: 'hidden',
        position: 'relative',
        width: dimensions.width,
      }}
    >
      <Box sx={{ bgcolor: card.color || '#dbeafe', p: 1 }}>
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
          <MultilineText text={getCardText(card)} />
        </Typography>
      </Box>
      {!card.collapsed &&
        getClusterChildLayout(card).map(({ card: childCard, rect }) => (
          <Box key={childCard.id} sx={{ left: rect.x, position: 'absolute', top: rect.y }}>
            {isClusterCard(childCard) ? (
              <ExportNestedClusterCard board={board} card={childCard} />
            ) : (
              <ExportCard board={board} card={childCard} isClusterChild />
            )}
          </Box>
        ))}
    </Box>
  );
}

function ExportCard({
  board,
  card,
  isClusterChild = false,
}: {
  board: WallBoard;
  card: WallCard;
  isClusterChild?: boolean;
}) {
  const shape = getCardShape(card);
  const dimensions = getCardFrameDimensions(card);
  const color = card.color || '#ffff99';
  const ownerName = getOwnerName(board, card);
  const positionStyles = isClusterChild
    ? { position: 'relative' as const }
    : {
        left: card.xPos ?? 0,
        position: 'absolute' as const,
        top: card.yPos ?? 0,
        zIndex: card.isLocked ? Math.max(card.zPos ?? 0, 3) : card.zPos ?? 3,
      };

  if (shape === 'circle') {
    return (
      <Box
        data-element-id={card.id}
        sx={{
          color: isDarkCard(color) ? '#fff' : '#222',
          filter: card.isLocked ? 'none' : 'drop-shadow(0 1px 0 rgba(0, 0, 0, 0.2))',
          height: dimensions.height ?? defaultShapeCardSize,
          minHeight: 100,
          minWidth: 100,
          width: dimensions.width,
          ...positionStyles,
        }}
      >
        <Box
          sx={{
            bgcolor: color,
            borderRadius: '50%',
            display: 'flex',
            height: '100%',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <ExportCardBody board={board} card={card} centered />
        </Box>
        <CardOwnerLabel ownerName={ownerName} placement="below" />
      </Box>
    );
  }

  if (shape === 'triangle') {
    const height = dimensions.height ?? defaultShapeCardSize;

    return (
      <Box
        data-element-id={card.id}
        sx={{
          color: isDarkCard(color) ? '#fff' : '#222',
          filter: card.isLocked ? 'none' : 'drop-shadow(0 1px 0 rgba(0, 0, 0, 0.2))',
          height,
          minHeight: 100,
          minWidth: 100,
          width: dimensions.width,
          ...positionStyles,
        }}
      >
        <Box
          sx={{
            borderBottom: `${height}px solid ${color}`,
            borderLeft: `${dimensions.width / 2}px solid transparent`,
            borderRight: `${dimensions.width / 2}px solid transparent`,
            height: 0,
            position: 'absolute',
            width: 0,
          }}
        />
        <Box sx={{ display: 'flex', height: '100%', position: 'relative', width: '100%' }}>
          <ExportCardBody board={board} card={card} centered triangle />
        </Box>
        <CardOwnerLabel ownerName={ownerName} placement="below" />
      </Box>
    );
  }

  return (
    <Box
      data-element-id={card.id}
      sx={{
        bgcolor: color,
        borderRadius: shape === 'rounded' ? '10px' : 0,
        boxShadow: card.isLocked ? 'none' : '0 1px 1px rgba(0, 0, 0, 0.15)',
        color: isDarkCard(color) ? '#fff' : '#222',
        display: 'flex',
        flexDirection: 'column',
        fontSize: 15,
        height: dimensions.height,
        minHeight: 38,
        overflow: shape === 'rounded' ? 'hidden' : 'visible',
        userSelect: 'none',
        width: dimensions.width ?? defaultRectangleCardWidth,
        ...positionStyles,
      }}
    >
      <ExportCardBody board={board} card={card} />
      <CardOwnerLabel ownerName={ownerName} />
    </Box>
  );
}

function ExportCardBody({
  board,
  card,
  centered = false,
  triangle = false,
}: {
  board: WallBoard;
  card: WallCard;
  centered?: boolean;
  triangle?: boolean;
}) {
  const cardText = getCardText(card);
  const cardHtml = getCardHtml(card);
  const titleText = getCardTitleText(card);
  const titleHtml = getCardTitleHtml(card);

  if (isImageCard(card)) {
    return (
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
        <ExportCardImage
          alt={cardText || 'Card image'}
          src={`${wallsApi.getCardImageUrl(board.id, card.id)}?random=${card.created ?? card.id}`}
        />
        {titleHtml ? (
          <Typography
            variant="body2"
            sx={imageTitleStyles(centered)}
            dangerouslySetInnerHTML={{ __html: titleHtml }}
          />
        ) : (
          titleText && (
            <Typography variant="body2" sx={imageTitleStyles(centered)}>
              <MultilineText text={titleText} />
            </Typography>
          )
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        alignItems: centered ? 'center' : 'flex-start',
        display: 'flex',
        flex: 1,
        justifyContent: centered ? 'center' : 'flex-start',
        minHeight: 38,
        p: triangle ? '42px 24px 18px' : 1.25,
        textAlign: centered ? 'center' : 'left',
        wordBreak: 'break-word',
      }}
    >
      {cardHtml ? (
        <Typography
          component="span"
          variant="body2"
          sx={{ color: 'inherit' }}
          dangerouslySetInnerHTML={{ __html: cardHtml }}
        />
      ) : (
        <Typography component="span" variant="body2" sx={{ color: 'inherit' }}>
          <MultilineText text={cardText} />
        </Typography>
      )}
    </Box>
  );
}

function ExportCardImage({ alt, src }: { alt: string; src: string }) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCurrent = true;

    setImageSrc(null);
    setIsLoading(true);

    loadCachedImage(src)
      .then((cachedImageUrl) => {
        if (isCurrent) {
          setImageSrc(cachedImageUrl);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [src]);

  return (
    <Box
      data-export-image-loading={isLoading ? 'true' : undefined}
      sx={{
        alignItems: 'center',
        display: 'flex',
        flex: '1 1 auto',
        height: '100%',
        justifyContent: 'center',
        minHeight: 80,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      }}
    >
      {imageSrc && (
        <Box
          component="img"
          alt={alt}
          src={imageSrc}
          draggable={false}
          sx={{
            display: 'block',
            height: '100%',
            inset: 0,
            minHeight: 80,
            objectPosition: 'center',
            position: 'absolute',
            width: '100%',
          }}
        />
      )}
    </Box>
  );
}

function getOwnerName(board: WallBoard, card: WallCard) {
  if (
    (!board.canOnlyEditOwn && !(board.showCardOwnerNames ?? true)) ||
    !card.ownerId ||
    card.isLocked
  ) {
    return '';
  }

  return board.participants.find((participant) => participant.id === card.ownerId)?.name ?? '';
}

function imageTitleStyles(centered: boolean) {
  return {
    color: 'inherit',
    flex: '0 0 auto',
    p: 1.25,
    textAlign: centered ? 'center' : 'left',
    wordBreak: 'break-word',
  };
}
